import { useRouter } from 'next/router';
import { useCallback } from 'react';
import {
  FLOWS,
  FLOW_ROUTES_AND_STEP_NUMBERS,
  SEEKER_INSTANT_BOOK_ROUTES,
  SEEKER_CHILD_CARE_ROUTES,
  SEEKER_DAYCARE_CHILD_CARE_ROUTES,
  CLIENT_FEATURE_FLAGS,
} from '@/constants';
import { useFlowState, useSeekerState, useSeekerCCState } from '@/components/AppState';
import useIsEarlyPremiumIntro from '@/components/hooks/useIsEarlyPremiumIntro';
import { useFeatureFlags } from '@/components/FeatureFlagsContext';
import useIsLoadingScreenDesign from '@/components/hooks/useIsLoadingScreenDesign';

function useFlowNavigation() {
  const { flowName } = useFlowState();
  const { zipcode } = useSeekerState();
  const { pathname, push } = useRouter();
  const { czenServiceIdForMember, czenTimeIntent } = useSeekerCCState();
  const { isEarlyPremiumIntroFlow } = useIsEarlyPremiumIntro();
  const featureFlags = useFeatureFlags();
  const { isRemoveRecapPageVariant } = useIsLoadingScreenDesign();
  const shouldShowSeekerMultiVertical =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_CAPTURE_MULTI_VERTICAL]?.variationIndex === 2;

  const goNext = useCallback(
    (skipNext?: boolean) => {
      const flow = FLOW_ROUTES_AND_STEP_NUMBERS[flowName];

      if (!flow) {
        return;
      }

      const paths = Object.keys(flow);
      let index = 1 + paths.findIndex((route) => route === pathname);
      if (skipNext) {
        index += 1;
      }
      let nextRoute = paths[index];

      if (flowName === FLOWS.SEEKER_CHILD_CARE.name) {
        if (czenTimeIntent && nextRoute === SEEKER_CHILD_CARE_ROUTES.CARE_DATE) {
          index += 1;
          nextRoute = paths[index];
        }

        if (pathname === SEEKER_CHILD_CARE_ROUTES.CARE_LOCATION) {
          const newFlowPaths = Object.keys(FLOW_ROUTES_AND_STEP_NUMBERS.SEEKER_CHILD_CARE);
          index += 1;
          nextRoute = newFlowPaths[index];
        }
      }

      if (pathname.indexOf('care-who') !== -1) {
        if (pathname.indexOf('/ib/') !== -1) {
          nextRoute = SEEKER_INSTANT_BOOK_ROUTES.WHAT_DAY_AND_TIME;
        } else if (shouldShowSeekerMultiVertical) {
          nextRoute = SEEKER_CHILD_CARE_ROUTES.CARE_MULTI_VERTICAL;
        } else if (isRemoveRecapPageVariant) {
          nextRoute = SEEKER_CHILD_CARE_ROUTES.ACCOUNT_CREATION_EMAIL;
        } else {
          nextRoute = SEEKER_CHILD_CARE_ROUTES.RECAP;
        }
      }

      if (
        pathname === SEEKER_DAYCARE_CHILD_CARE_ROUTES.PHONE_VERIFICATION &&
        !isEarlyPremiumIntroFlow
      ) {
        nextRoute = SEEKER_DAYCARE_CHILD_CARE_ROUTES.RECOMMENDATIONS;
      }

      if (!nextRoute) {
        return;
      }

      push(nextRoute);
    },
    [push, pathname, flowName, czenServiceIdForMember, czenTimeIntent, zipcode]
  );

  return { goNext };
}

export default useFlowNavigation;
